"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { Roboto } from "next/font/google";
import { FadeInUp } from "@/components/animations/FadeInUp";

const roboto = Roboto({
  subsets: ["latin"],
  weight: ["300", "400", "500", "700", "900"],
  display: "swap",
});

const TAGS = ["MSME / Udyam", "Startup India", "ISO", "FSSAI", "GeM"];

const STATS = [
  { v: "10+", l: "Certifications Offered" },
  { v: "1,000+", l: "Registrations Done" },
  { v: "Free", l: "Initial Assessment" },
  { v: "100%", l: "End-to-End Managed" },
];

const MSME_BENEFITS = [
  { title: "Priority Credit Access", detail: "Collateral-free loans with subsidised interest rates under CGTMSE" },
  { title: "Interest Rate Subsidy", detail: "2–3% interest subvention on eligible credit facilities" },
  { title: "Credit Guarantee Cover", detail: "Government-backed guarantee for loans up to ₹2 crore without collateral" },
  { title: "Tax Benefits", detail: "Presumptive taxation, delayed payment protection under MSMED Act" },
  { title: "Government Tender Preference", detail: "Exemption from EMD, relaxed eligibility norms in public procurement" },
  { title: "Market Development Support", detail: "Subsidised participation in trade fairs, exhibitions and GeM marketplace" },
  { title: "Technology Upgradation", detail: "Capital subsidy for machinery and technology upgrade under CLCSS" },
  { title: "Enhanced Credibility", detail: "Formal recognition builds trust with banks, investors and large corporates" },
];

const STARTUP_BENEFITS = [
  "Income tax exemption for 3 consecutive years under Section 80-IAC",
  "Angel tax exemption on funding received from eligible investors",
  "80% rebate on patent filing fees with fast-track examination",
  "Access to Startup India Seed Fund — up to ₹20 lakh for validation",
  "Self-certification for 9 labour and 3 environmental laws",
  "Exemption from EMD in government tenders under public procurement policy",
  "Networking, mentorship and visibility through government startup platforms",
  "Access to incubators, accelerators and government-backed funding programs",
];

const ADD_CERTS = [
  { cert: "ISO 9001:2015 — Quality Management", covers: "International quality management standard for products and services" },
  { cert: "ISO 14001 — Environmental Management", covers: "Environmental compliance and sustainability certification" },
  { cert: "ISO 27001 — Information Security", covers: "Information security management for IT and data-driven businesses" },
  { cert: "ZED Certification", covers: "Manufacturing quality and sustainability — improves global competitiveness" },
  { cert: "FSSAI Registration / Licence", covers: "Food safety certification for food manufacturers, traders and restaurants" },
  { cert: "GeM Registration", covers: "Government eMarketplace — sell directly to government buyers" },
  { cert: "NSIC Registration", covers: "National Small Industries Corporation — benefits for government procurement" },
  { cert: "Tax Exemption (80-IAC, 12AB, 80G)", covers: "Income tax exemption for eligible startups and NGOs/Section 8 companies" },
  { cert: "NIDHI Company Registration", covers: "Mutual benefit financial company under Companies Act 2013" },
  { cert: "DPIIT Registration (Startup)", covers: "Core startup recognition enabling all Startup India benefits" },
];

const STEPS = [
  { n: "01", title: "Free Consultation", body: "We review your business profile and identify all applicable certifications and the benefits each unlocks." },
  { n: "02", title: "Eligibility Assessment", body: "We confirm eligibility and advise on the best certification sequence for your business stage." },
  { n: "03", title: "Documentation Preparation", body: "We compile all required documents to specification — nothing missed, nothing delayed." },
  { n: "04", title: "Portal Registration", body: "We guide you through official registration portals step by step, handling the technical complexity." },
  { n: "05", title: "Compliance Verification", body: "We review all information thoroughly to minimise rejection risk before submission." },
  { n: "06", title: "Submission & Follow-Up", body: "We submit your application through official channels and track its status to completion." },
  { n: "07", title: "Post-Registration Guidance", body: "We help you leverage your certifications through scheme applications and subsidy claims." },
];

const FAQS = [
  { q: "What is MSME / Udyam Registration?", a: "MSME (Udyam) Registration is a government-issued recognition that classifies your enterprise as Micro, Small, or Medium. It is free to obtain and unlocks a wide range of financial benefits, subsidies and competitive advantages." },
  { q: "Who is eligible for Startup India (DPIIT)?", a: "Companies incorporated less than 10 years ago with annual turnover under ₹100 crore, working on an innovative product or scalable business model and not formed by splitting an existing entity." },
  { q: "How long does ISO certification take?", a: "ISO certification typically takes 3–6 months including gap analysis, implementation, internal audit and third-party certification audit. We can help accelerate the process." },
  { q: "Is ISO certification mandatory?", a: "ISO is voluntary but strongly recommended for credibility, quality assurance and to meet vendor compliance requirements of large corporates and government contracts." },
  { q: "Do food businesses need FSSAI?", a: "Yes. FSSAI registration or licence is mandatory for all businesses involved in food manufacturing, processing, trading, distribution, or retail in India." },
  { q: "What is GeM registration and who needs it?", a: "GeM (Government eMarketplace) registration allows businesses to sell directly to government buyers. Any MSME, startup, or established company can register and bid for government procurement orders." },
];

function Divider({ className }: { className?: string }) {
  return <div className={`w-12 h-[3px] bg-[#1565C0] mb-7 ${className ?? ""}`} />;
}

function FaqItem({ q, a, i }: { q: string; a: string; i: number }) {
  const [open, setOpen] = useState(false);

  return (
    <FadeInUp delay={i * 0.05} className="border-b border-[#E2E8F0] last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-4 py-5 text-left"
      >
        <span className="text-[15px] font-semibold text-[#0F172A] leading-[1.5]">{q}</span>
        <span
          className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors duration-200 ${
            open ? "bg-[#1565C0] text-white" : "bg-[#F8FAFC] text-[#1565C0]"
          }`}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            {open ? (
              <path d="M2 6H10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            ) : (
              <>
                <path d="M6 2V10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M2 6H10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </>
            )}
          </svg>
        </span>
      </button>

      {open && <p className="pb-5 text-[14px] leading-[1.85] text-[#64748B]">{a}</p>}
    </FadeInUp>
  );
}

function Step({ s, i, last }: { s: { n: string; title: string; body: string }; i: number; last: boolean }) {
  return (
    <FadeInUp
      delay={(i % 4) * 0.09}
      className={`relative flex gap-4 ${last ? "pb-0" : "pb-7"}`}
    >
      {!last && <span className="absolute left-[17px] top-[36px] h-[calc(100%-46px)] w-px bg-[#E2E8F0]" />}
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#1565C0] flex items-center justify-center">
        <span className="text-[11px] font-semibold text-white">{s.n}</span>
      </div>
      <div className="pt-[6px]">
        <div className="text-[14px] font-semibold text-[#0F172A] mb-1.5">{s.title}</div>
        <p className="text-[13.5px] leading-[1.75] text-[#64748B]">{s.body}</p>
      </div>
    </FadeInUp>
  );
}

function BenefitRow({ b, i }: { b: { title: string; detail: string }; i: number }) {
  return (
    <FadeInUp delay={(i % 4) * 0.07} className="grid grid-cols-[auto_1fr] gap-4 py-4 border-b border-[#E2E8F0] items-start">
      <div className="flex items-start gap-2">
        <span className="mt-1.5 block w-2.5 h-2.5 rounded-full bg-[#1565C0]" />
        <span className="text-[13px] font-semibold text-[#0F172A] leading-[1.5]">{b.title}</span>
      </div>
      <span className="text-[13px] text-[#64748B] leading-[1.6]">{b.detail}</span>
    </FadeInUp>
  );
}

function CertificationRow({ c, i }: { c: { cert: string; covers: string }; i: number }) {
  return (
    <FadeInUp
      delay={(i % 5) * 0.06}
      className={`table-row ${i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}`}
    >
      <td className="px-5 py-4 align-top font-semibold text-[#0F172A] text-[14px] leading-[1.4] whitespace-nowrap">
        <div className="flex items-start gap-2">
          <span className="mt-2 block w-1.5 h-1.5 rounded-full bg-[#1565C0]" />
          {c.cert}
        </div>
      </td>
      <td className="px-5 py-4 align-top text-[13.5px] text-[#64748B] leading-[1.55]">{c.covers}</td>
    </FadeInUp>
  );
}

function CheckIcon() {
  return (
    <div className="w-5 h-5 rounded-full bg-[#1565C0] flex items-center justify-center flex-shrink-0 mt-1">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

export default function CertificationPage() {
  return (
    <main className={`${roboto.className} bg-white`}> 
      <section className="grid lg:grid-cols-2 min-h-0 lg:min-h-[580px]">
        <div className="flex flex-col justify-center bg-white py-[30px] pr-4 pl-4 sm:pr-[30px] sm:pl-8 lg:pl-[80px]">
          <div className="flex flex-wrap items-center gap-2 text-[12px] text-[#64748B] mb-6">
            <Link href="/services" className="text-[#1565C0] font-medium hover:underline">
              Services
            </Link>
            <span className="text-[#E2E8F0]">›</span>
            <span>Certification Assistance</span>
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            {TAGS.map((tag) => (
              <span key={tag} className="text-[11px] font-semibold text-[#1565C0] bg-[#EFF6FF] py-1 px-2.5 tracking-[0.04em] rounded-full">
                {tag}
              </span>
            ))}
          </div>

          <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">
            Government & Industry Certifications
          </span>
          <Divider />
          <h1 className="font-extrabold text-[#0F172A] text-[clamp(26px,3.5vw,46px)] leading-[1.1] mb-5 max-w-[38rem]">
            Get Certified. Unlock Benefits. Grow Faster.
          </h1>
          <p className="text-[16px] leading-[1.85] text-[#64748B] mb-9 ">
            Official business certifications open doors to government funding, tax benefits, tender preferences and institutional credibility. We handle the entire process — so you get the recognition you deserve, faster.
          </p>
          <div className="flex flex-wrap gap-3">
            <button className="inline-flex items-center justify-center rounded-sm bg-[#1565C0] text-white px-7 py-3 text-[13px] font-bold tracking-[0.06em]">
              GET FREE ASSESSMENT
            </button>
            <button className="inline-flex items-center justify-center rounded-sm border border-[#E2E8F0] bg-transparent text-[#0F172A] px-7 py-3 text-[13px] font-semibold tracking-[0.06em]">
              VIEW ALL CERTIFICATIONS
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden w-full min-h-[250px] sm:min-h-[340px] lg:min-h-[420px]">
          <Image
            src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=900&q=85"
            alt="Certification assistance"
            fill
            className="object-cover"
          />
        </div>
      </section>

      <div className="bg-[#1565C0] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {STATS.map((stat, i) => (
          <div key={i} className={i < 3 ? "py-5 text-center lg:border-r border-white/15" : "py-5 text-center"}>
            <div className="text-[24px] font-black text-white leading-none">{stat.v}</div>
            <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-white/70 mt-1">{stat.l}</div>
          </div>
        ))}
      </div>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 items-start">
          <FadeInUp className="space-y-5">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0]">MSME / Udyam Registration</span>
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18]">
              Transform Your Business with MSME Recognition
            </h2>
            <p className="text-[15px] leading-[1.85] text-[#64748B] mb-8 max-w-xl">
              MSME Certification (officially Udyam Registration) is a government-issued recognition that classifies your enterprise as Micro, Small, or Medium based on investment and turnover. This single registration unlocks a wide range of financial benefits, government schemes and competitive advantages.
            </p>
            <div className="relative w-full h-[160px] sm:h-[200px] lg:h-[240px] overflow-hidden rounded-sm">
              <Image
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=700&q=80"
                alt="MSME registration"
                fill
                className="object-cover"
              />
            </div>
          </FadeInUp>

          <FadeInUp delay={0.12} className="pt-2">
            <div className="text-[12px] font-semibold uppercase tracking-[0.1em] text-[#64748B] mb-2.5">
              8 Key Benefits
            </div>
            <div className="border-t-2 border-[#1565C0]">
              {MSME_BENEFITS.map((benefit, index) => (
                <BenefitRow key={index} b={benefit} i={index} />
              ))}
            </div>
          </FadeInUp>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 items-center">
          <FadeInUp className="relative">
            <div className="relative w-full h-[260px] sm:h-[360px] lg:h-[460px] overflow-hidden rounded-sm">
              <Image
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=700&q=80"
                alt="Startup India"
                fill
                className="object-cover"
              />
            </div>
            <div className="hidden lg:block absolute -bottom-6 -right-6 w-72 rounded-sm border border-[#E2E8F0] bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.10)]">
              <div className="text-[30px] font-black text-[#1565C0] leading-none">₹20L</div>
              <div className="text-[11px] uppercase tracking-[0.06em] text-[#64748B] font-medium mt-1">Seed Fund Access</div>
              <div className="text-[28px] font-black text-[#E65100] leading-none mt-4">3 Years</div>
              <div className="text-[11px] uppercase tracking-[0.06em] text-[#64748B] font-medium mt-1">Tax Exemption</div>
            </div>
          </FadeInUp>

          <FadeInUp delay={0.12} className="space-y-5">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0]">Startup India / DPIIT Recognition</span>
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18]">
              Fuel Your Startup with Government Backing
            </h2>
            <p className="text-[15px] leading-[1.85] text-[#64748B] mb-8 max-w-xl">
              DPIIT recognition under Startup India unlocks exclusive advantages designed to accelerate early-stage business growth — from tax exemptions to fast-track funding and simplified compliance.
            </p>
            <div className="space-y-3">
              {STARTUP_BENEFITS.map((point, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckIcon />
                  <span className="text-[13.5px] text-[#475569] leading-[1.7]">{point}</span>
                </div>
              ))}
            </div>
          </FadeInUp>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto">
          <FadeInUp className="mb-10 lg:mb-14">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0]">Additional Certifications</span>
            <Divider />
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18] max-w-2xl">
                10 More Certifications We Handle
              </h2>
              <p className="text-[14px] leading-[1.85] text-[#64748B] max-w-[360px]">
                From ISO standards to GeM and FSSAI — we manage the full certification landscape for Indian businesses.
              </p>
            </div>
          </FadeInUp>

          <FadeInUp>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-[#1565C0]">
                  <tr>
                    {['Certification', 'What It Covers'].map((heading) => (
                      <th
                        key={heading}
                        className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.1em] text-white"
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ADD_CERTS.map((cert, index) => (
                    <CertificationRow key={index} c={cert} i={index} />
                  ))}
                </tbody>
              </table>
            </div>
          </FadeInUp>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 items-start">
          <FadeInUp className="space-y-5">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0]">How We Help</span>
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18]">
              7-Step Certification Process
            </h2>
            <p className="text-[15px] leading-[1.85] text-[#64748B] mb-10 max-w-xl">
              We manage the entire certification journey — from identifying what you qualify for to helping you claim every benefit after registration.
            </p>
            <div className="relative w-full h-[160px] sm:h-[200px] lg:h-[260px] overflow-hidden rounded-sm">
              <Image
                src="https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=700&q=80"
                alt="Our process"
                fill
                className="object-cover"
              />
            </div>
          </FadeInUp>

          <div>
            {STEPS.map((step, index) => (
              <Step key={index} s={step} i={index} last={index === STEPS.length - 1} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 items-start">
          <FadeInUp className="space-y-5">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0]">Frequently Asked Questions</span>
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18]">
              Common Questions About Business Certifications
            </h2>
            <p className="text-[15px] leading-[1.85] text-[#64748B] max-w-xl">
              Everything you need to know before starting your certification journey with Farsight Solutions.
            </p>
            <div className="relative w-full h-[180px] sm:h-[240px] lg:h-[280px] overflow-hidden rounded-sm">
              <Image
                src="https://images.unsplash.com/photo-1560472355-536de3962603?w=700&q=80"
                alt="Certification FAQ"
                fill
                className="object-cover"
              />
            </div>
          </FadeInUp>

          <div className="border-t border-[#E2E8F0] pt-8">
            {FAQS.map((faq, index) => (
              <FaqItem key={index} q={faq.q} a={faq.a} i={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 items-center">
          <FadeInUp>
            <div className="w-full h-[220px] sm:h-[300px] lg:h-[380px] relative overflow-hidden rounded-sm">
              <Image
                src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80"
                alt="Get certified"
                fill
                className="object-cover"
              />
            </div>
          </FadeInUp>

          <FadeInUp delay={0.1} className="space-y-5">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0]">Ready to Get Certified?</span>
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18]">
              Get a Free Certification Assessment
            </h2>
            <p className="text-[15px] leading-[1.85] text-[#64748B]">
              We identify all certifications your business qualifies for and the benefits each unlocks — at no cost, no commitment.
            </p>
            <p className="text-[15px] leading-[1.85] text-[#64748B]">
              Our experts assess your eligibility across 10+ certifications in the first session and give you a clear roadmap to maximum benefits.
            </p>
            <div className="flex flex-wrap gap-3">
              <button className="inline-flex items-center justify-center rounded-sm bg-[#1565C0] text-white px-8 py-3 text-[13px] font-bold tracking-[0.06em]">
                GET FREE ASSESSMENT
              </button>
              <button className="inline-flex items-center justify-center rounded-sm border border-[#E2E8F0] bg-transparent text-[#0F172A] px-8 py-3 text-[13px] font-semibold tracking-[0.06em]">
                VIEW ALL CERTIFICATIONS
              </button>
            </div>
          </FadeInUp>
        </div>
      </section>
    </main>
  );
}
